import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// eslint-disable-next-line max-len
export const URL_VALIDATOR_REGEX_SIMPLE =
  /^(?!.*Alt \+\s*\d)(?:http(s)?:\/\/)?(?:www\.)?[a-z0-9\-_.]{2,256}(?:\.[A-Za-z]{2,6}){1}(?:\/[a-zA-Z0-9\-_/?!&{}()@*-+"'€$<>^[\]/:%|§\\#=,.]*)?$/;

export const DATETIME_VALIDATOR_KEY = 'datetime';
export const DATE_VALIDATOR_KEY = 'date';
export const TIME_VALIDATOR_KEY = 'time';

export class DateValidators {
  /**
   * Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je čas.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je čas.
   */
  static isTime(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl.value || ctrl.value instanceof Date) return null;
      return { [TIME_VALIDATOR_KEY]: true };
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je datum.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je datum.
   */
  static isDate(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl.value || ctrl.value instanceof Date) return null;
      return { [DATE_VALIDATOR_KEY]: true };
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je datum a čas.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku je datum a čas.
   */
  static isDateTime(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      if (!ctrl.value || ctrl.value instanceof Date) return null;
      return { [DATETIME_VALIDATOR_KEY]: true };
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota data v koncovém ovládacím prvku je větší než hodnota data v počátečním ovládacím prvku.
   *
   * @param {string} startControlKey - Klíč počátečního ovládacího prvku.
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota data v koncovém ovládacím prvku je větší než hodnota data v počátečním ovládacím prvku.
   */
  static greaterThan(startControlKey: string): ValidatorFn {
    return (endControl: AbstractControl): ValidationErrors | null => {
      const startDate: Date = endControl.parent?.get(startControlKey)?.value;
      const endDate: Date = endControl.value;

      if (!startDate || !endDate) return null;
      if (startDate >= endDate) return { greaterThan: true };

      return null;
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku je menší než všechny hodnoty v zadaném poli dat.
   *
   * @param {(string | null)[]} dates - Pole dat, se kterými se porovnává hodnota ovládacího prvku.
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku je menší než všechny hodnoty v zadaném poli dat.
   */
  static smallerThanEvery(dates: (string | null)[]): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const value: Date = ctrl.value;
      const arr = (dates || []).filter(Boolean);
      const res = {
        smallerThanEvery: true,
      };

      try {
        const valid = arr.every((a) => {
          if (!a) return true;

          const d = new Date(a);
          return value < d;
        });

        return valid ? null : res;
      } catch (e) {
        return res;
      }
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku je v budoucnosti.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku je v budoucnosti.
   */
  static inFuture(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const now = new Date();
      const value: Date = ctrl.value;
      const res = {
        inFuture: true,
      };

      try {
        return value > now ? null : res;
      } catch (e) {
        return res;
      }
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku není v minulosti.
   *
   * Tento validátor porovnává pouze část data (ignoruje čas) hodnoty ovládacího prvku
   * s aktuálním datem. Pokud je datum ovládacího prvku v minulosti, vrátí objekt chyby validace
   * s klíčem `notInPast`. Jinak vrátí `null`.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota data v ovládacím prvku není v minulosti.
   */
  static notInPast(): ValidatorFn {
    return (ctrl: AbstractControl): ValidationErrors | null => {
      const now = new Date();
      // Nastaví hodiny, minuty, sekundy a milisekundy na nulu pro aktuální datum i hodnotu ovládacího prvku
      now.setHours(0, 0, 0, 0);

      const value: Date = new Date(ctrl.value);
      value.setHours(0, 0, 0, 0);

      const res = {
        notInPast: true,
      };

      try {
        // Porovnává pouze část data (ignoruje čas)
        return value >= now ? null : res;
      } catch (e) {
        return res;
      }
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota data v koncovém ovládacím prvku je větší nebo rovna hodnotě data v počátečním ovládacím prvku.
   *
   * @param {string} startControlKey - Klíč počátečního ovládacího prvku.
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota data v koncovém ovládacím prvku je větší nebo rovna hodnotě data v počátečním ovládacím prvku.
   */
  static greaterEqualDayThan(startControlKey: string): ValidatorFn {
    return (endControl: AbstractControl): ValidationErrors | null => {
      const startDate: Date = endControl.parent?.get(startControlKey)?.value;
      const endDate: Date = endControl.value;

      if (!startDate || !endDate) return null;

      startDate.setHours(0);
      startDate.setMinutes(0);
      startDate.setUTCSeconds(0);
      endDate.setHours(0);
      endDate.setMinutes(0);
      endDate.setUTCSeconds(0);

      if (startDate > endDate) return { greaterThan: true };

      return null;
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda je rozsah dat v ovládacím prvku prázdný.
   *
   * @param {AbstractControl} control - Ovládací prvek, který se kontroluje.
   * @returns {ValidationErrors | null} Objekt chyby validace, pokud je rozsah dat prázdný, jinak `null`.
   */
  static dateRangeRequired(control: AbstractControl): ValidationErrors | null {
    return isEmptyDateRange(control) ? { required: true } : null;
  }
}

export class TextValidators {
  /**
   * Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku neobsahuje mezery.
   *
   * @returns {ValidatorFn} Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku neobsahuje mezery.
   */
  static noWhitespaces(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const isWhitespace = (control.value || '').trim().length === 0;
      return isWhitespace ? { noWhitespace: true } : null;
    };
  }

  /**
   * Funkce validátoru, která kontroluje, zda hodnota v ovládacím prvku obsahuje alespoň min. počet znaků bez bílých znaků.
   * @param min Počet znaků bez mezer
   */
  static minNonWhitespaceLength(min: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value || '';
      const nonWhitespaceLength = value.replace(/\s/g, '').length;
      return nonWhitespaceLength >= min
        ? null
        : { minNonWhitespaceLength: { requiredLength: min, actualLength: nonWhitespaceLength } };
    };
  }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isEmptyDateRange(control: any): boolean {
  if (control.value) {
    if (control.fromKey && control.value[control.fromKey]) {
      return false;
    }
    if (control.toKey && control.value[control.toKey]) {
      return false;
    }
  }
  return true;
}

export function isUrlValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    const urlRegex = new RegExp(URL_VALIDATOR_REGEX_SIMPLE, 'i');
    const valid = urlRegex.test(value);
    return valid ? null : { invalidUrl: { value: control.value } };
  };
}

export function maxLengthHtmlSourceRichEditorValidator(maxLength: number): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value || ''; // Ošetření prázdné hodnoty

    return value.length > maxLength
      ? {
          maxlengthHtmlSource: {
            requiredLength: maxLength,
            actualLength: value.length,
            // message: `Maximální povolený počet znaků je ${maxLength}. Aktuální počet znaků: ${value.length}.`,
          },
        }
      : null;
  };
}

/**
 * Validator for Quill HTML content: returns an error if the content is "empty"
 * (only <p><br></p> or whitespace inside HTML).
 */
export function quillRequired(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const html = control.value as string;

    if (!html) return { required: true };

    // Remove all HTML tags and decode spaces
    const textOnly = html
      .replace(/<[^>]*>/g, '')
      .replace(/\u00a0/g, '')
      .trim();

    // Consider empty if original HTML is <p><br></p> or if all text is whitespace
    const isEmpty = /^\s*<p><br><\/p>\s*$/i.test(html) || textOnly.length === 0;

    return isEmpty ? { required: true } : null;
  };
}

export function validRegexValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null;
    }
    try {
      new RegExp(value);
      return null;
    } catch (error) {
      return { pattern: true };
    }
  };
}
